"use client";

import { Input } from "@/components/ui";
import { NEWSLETTER_API } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { Loader2Icon, MailIcon } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

const NewsletterSubscribeInputField = ({
	classNames,
}: {
	classNames?: { container?: string; inputContainer?: string; button?: string };
}) => {
	const [email, setEmail] = useState<string>("");
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value);
		setErrorMessage("");
	};

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		try {
			setIsLoading(true);

			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!email.trim()) {
				setErrorMessage("Please enter an email");
				setIsLoading(false);
				return;
			}
			if (!emailRegex.test(email)) {
				setErrorMessage("Please enter a valid email");
				setIsLoading(false);
				return;
			}

			const url = new URL(NEWSLETTER_API);
			const response = await fetch(url, {
				method: "POST",
				body: JSON.stringify({ email }),
			});

			const data = await response.json();
			const message = data?.message;

			if (response.ok) {
				toast.success(message);
			} else {
				toast.error(message);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setEmail("");
			setIsLoading(false);
		}
	};

	return (
		<section className={cn(classNames?.container)}>
			<form id="newsletterForm" onSubmit={onSubmit}>
				<div className={cn("flex gap-2 max-md:flex-wrap", classNames?.inputContainer)}>
					<Input
						type="email"
						id="email"
						name="email"
						value={email}
						placeholder="Enter your email"
						classNames={{
							inputContainer: "shadow-none focus-within:ring-1 focus-within:ring-red-600",
							label: "mb-0",
							container: "w-full sm:max-w-sm",
						}}
						prefix={
							<span className="py-2 pl-3">
								<MailIcon className="h-6 w-6 stroke-red-600 sm:h-8 sm:w-8" />
							</span>
						}
						onChange={onChange}
						autoComplete="email"
						required
					/>

					<button
						className={cn(
							"text-md flex h-full w-full items-center justify-center gap-1 rounded-md bg-red-600 px-6 py-3 font-semibold text-white hover:bg-red-700 sm:w-fit",
							classNames?.button
						)}
						disabled={isLoading}
					>
						{isLoading ? (
							<Loader2Icon className="h-4 w-4 animate-spin stroke-white" />
						) : (
							<>
								<span>Subscribe</span>
							</>
						)}
					</button>
				</div>
			</form>
			<p
				className={cn(
					"text-sm text-red-400 transition-opacity duration-150 ease-linear",
					errorMessage ? "opacity-100" : "opacity-0"
				)}
			>
				{errorMessage}
			</p>
		</section>
	);
};

export default NewsletterSubscribeInputField;
